import { useState, useEffect, useCallback, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from 'api/api'
import { useEventBus } from './useEventBus'
import type { EventBusPayload } from 'services/eventBusService'
import { Subscription } from 'rxjs'
import { filter } from 'rxjs/operators'

export interface TemporalWorkflowProgress {
    message: string
    timestamp: number
}

export interface TemporalWorkflowStatus {
    status: 'running' | 'completed' | 'failed'
    workflow_id: string
    run_id: string
    query_name: string
    workflow_class: string
    result?: TemporalWorkflowProgress[]
    error?: string
}

export interface UseTemporalWorkflowStatusOptions {
    enabled: boolean
    pollInterval?: number
    onComplete?: (result: TemporalWorkflowStatus) => void
    /** Terminal failure reported by the workflow itself. Falls back to onError. */
    onWorkflowError?: (error: string) => void
    /** Query/transport failure while reading status. */
    onError?: (error: string) => void
    /** Keep the fallback poll alive after a non-timeout query/transport failure. */
    continuePollingOnError?: boolean
    eventBusTopic?: string // EventBus topic for real-time updates
    logPrefix?: string // Prefix for console logging
    accessToken?: string // Access token for secure workflow status queries
}

export function useTemporalWorkflowStatus(
    workflowId: string | null,
    runId: string | null,
    queryName: string = 'progress',
    workflowClass: string,
    options: UseTemporalWorkflowStatusOptions = { enabled: false },
) {
    const [status, setStatus] = useState<TemporalWorkflowStatus | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [isPolling, setIsPolling] = useState(false)
    const [eventBusEnabled, setEventBusEnabled] = useState(true)
    const eventBusSubscription = useRef<Subscription | null>(null)

    const {
        enabled,
        pollInterval = 1000,
        onComplete,
        onWorkflowError,
        onError,
        continuePollingOnError = false,
        eventBusTopic,
        logPrefix = 'Workflow',
        accessToken,
    } = options

    const fetchStatus = useCallback(async () => {
        if (!workflowId || !runId || !enabled) {
            return
        }

        try {
            setIsLoading(true)
            setError(null)

            const response = await api.getTemporalWorkflowStatus(
                workflowId,
                runId,
                queryName,
                workflowClass,
                accessToken,
            )

            setStatus(response)

            // Show only the latest progress message
            const progress = response.result || []
            const lastMessage = progress[progress.length - 1]

            if (lastMessage?.message && logPrefix) {
                console.log(`${logPrefix}:`, lastMessage.message)
            }

            // Check if workflow is completed based on completed flag
            if (lastMessage?.completed === true) {
                setIsPolling(false)
                if (lastMessage.error === true) {
                    setError(lastMessage.message)
                    if (onWorkflowError) onWorkflowError(lastMessage.message)
                    else onError?.(lastMessage.message)
                } else {
                    onComplete?.(response)
                }
            }

            return response
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'

            // Don't stop polling for timeout errors - let react-query handle retries
            const isTimeoutError =
                errorMessage.includes('query timed out') ||
                errorMessage.includes('timeout') ||
                errorMessage.includes('DEADLINE_EXCEEDED')

            if (!isTimeoutError) {
                // Most consumers treat a query failure as final. Workflows whose
                // polling is a durable EventBus fallback can keep trying instead.
                setError(errorMessage)
                if (!continuePollingOnError) setIsPolling(false)
                onError?.(errorMessage)
            } else {
                // For timeout errors, just log but continue polling
                // Temporal query timeout, will retry automatically
            }

            throw err
        } finally {
            setIsLoading(false)
        }
    }, [
        workflowId,
        runId,
        queryName,
        workflowClass,
        enabled,
        onComplete,
        onWorkflowError,
        onError,
        continuePollingOnError,
        logPrefix,
        accessToken,
    ])

    const startPolling = useCallback(() => {
        if (!workflowId || !runId || isPolling) {
            return
        }
        setIsPolling(true)
        setError(null)
    }, [workflowId, runId, isPolling])

    const stopPolling = useCallback(() => {
        setIsPolling(false)
    }, [])

    // A new run is a new story: nothing the previous run reported (least of
    // all its final error frame) describes it.
    useEffect(() => {
        setStatus(null)
        setError(null)
    }, [workflowId, runId])

    // Old WebSocket approach removed - now using EventBus for real-time updates

    // Use EventBus for real-time updates (new approach)
    const { getTopic$, isConnected: isEventBusConnected } = useEventBus()

    useEffect(() => {
        // Only use EventBus if topic is provided and EventBus is enabled
        if (!enabled || !workflowId || !runId || !eventBusEnabled || !eventBusTopic) {
            return
        }

        // Wait for EventBus connection
        if (!isEventBusConnected) {
            return
        }

        // Subscribe to the specified EventBus topic
        eventBusSubscription.current = getTopic$(eventBusTopic)
            .pipe(
                filter((payload: EventBusPayload) => {
                    // EventBus messages have structure: { topic, source, timestamp, payload: { workflow_id, run_id, ... } }
                    const eventPayload = (payload.payload || payload) as EventBusPayload
                    const matches =
                        eventPayload.workflow_id === workflowId && eventPayload.run_id === runId

                    return matches
                }),
            )
            .subscribe({
                next: payload => {
                    // Status message already logged in eventBusService
                    // EventBus payload received

                    // Update local state with EventBus data
                    // The EventBus message structure: { topic, source, timestamp, payload: { workflow_id, run_id, status, completed, ... } }
                    const eventPayload = payload.payload || payload // Handle both direct and nested payload
                    // Check for error flag in both status object and top-level (workflows send it at top level)
                    const isError =
                        eventPayload.status?.error === true || eventPayload.error === true
                    const isCompleted =
                        eventPayload.completed === true || eventPayload.status?.completed === true

                    setStatus(prev => ({
                        ...prev,
                        status: isCompleted ? (isError ? 'failed' : 'completed') : 'running',
                        workflow_id: eventPayload.workflow_id,
                        run_id: eventPayload.run_id,
                        query_name: queryName,
                        workflow_class: workflowClass,
                        result: [...(prev?.result || []), eventPayload.status || eventPayload],
                        error: isError
                            ? eventPayload.status?.message || eventPayload.message
                            : undefined,
                    }))

                    // Check for completion (either success or error)
                    if (isCompleted) {
                        setIsPolling(false)
                        const errorMessage = eventPayload.status?.message || eventPayload.message
                        const finalStatusValue = isError ? 'failed' : 'completed'
                        const finalStatus = {
                            status: finalStatusValue as 'failed' | 'completed',
                            workflow_id: eventPayload.workflow_id,
                            run_id: eventPayload.run_id,
                            query_name: queryName,
                            workflow_class: workflowClass,
                            result: [
                                ...(status?.result || []),
                                eventPayload.status || eventPayload,
                            ],
                            error: isError ? errorMessage : undefined,
                        }
                        setStatus(finalStatus)

                        if (isError) {
                            setError(errorMessage)
                            if (onWorkflowError) onWorkflowError(errorMessage)
                            else onError?.(errorMessage)
                        } else {
                            onComplete?.(finalStatus)
                        }
                    }
                },
                error: err => {
                    console.error(
                        '❌ [useTemporalWorkflowStatus] EventBus subscription error:',
                        err,
                    )
                    setEventBusEnabled(false)
                    // Don't call onError here as it's just a transport failure, not a workflow error
                },
            })

        return () => {
            if (eventBusSubscription.current) {
                eventBusSubscription.current.unsubscribe()
                eventBusSubscription.current = null
            }
        }
    }, [
        enabled,
        workflowId,
        runId,
        workflowClass,
        eventBusEnabled,
        isEventBusConnected,
        getTopic$,
        queryName,
        onComplete,
        onWorkflowError,
        onError,
        status,
        eventBusTopic,
    ])

    // Use react-query for polling as fallback to catch completed workflows if EventBus message is missed
    useQuery({
        queryKey: [
            'temporal-workflow-status',
            workflowId,
            runId,
            queryName,
            workflowClass,
            isPolling,
        ],
        queryFn: fetchStatus,
        enabled: !!(isPolling && enabled && workflowId && runId),
        refetchInterval: eventBusEnabled && isEventBusConnected ? 5000 : 500, // Slower polling when EventBus is active
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: (failureCount, error) => {
            // Retry temporal timeout errors up to 3 times, but not other errors
            if (failureCount < 3) {
                const errorMessage = error instanceof Error ? error.message : String(error)
                if (
                    errorMessage.includes('query timed out') ||
                    errorMessage.includes('timeout') ||
                    errorMessage.includes('DEADLINE_EXCEEDED')
                ) {
                    console.log(
                        `Temporal query timeout (attempt ${failureCount + 1}/3), retrying...`,
                    )
                    return true
                }
            }
            return false
        },
        retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 3000), // Exponential backoff: 1s, 2s, 3s
    })

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            setIsPolling(false)
            if (eventBusSubscription.current) {
                eventBusSubscription.current.unsubscribe()
                eventBusSubscription.current = null
            }
        }
    }, [])

    return {
        status,
        isLoading,
        error,
        isPolling,
        startPolling,
        stopPolling,
        fetchStatus,
        progress: status?.result || [],
        latestMessage: status?.result?.[status.result.length - 1]?.message || null,
        isCompleted: status?.status === 'completed',
    }
}
